import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import { mediaUrl } from "../utils/wsUrl";
import "./ClientFeatures.css";

const KINDS = [
  { id: "all", label: "All" },
  { id: "image", label: "Images" },
  { id: "video", label: "Videos" },
  { id: "audio", label: "Audio" },
  { id: "document", label: "Docs" },
  { id: "file", label: "Other" },
];

const kindIcon = (kind) => {
  if (kind === "image") return "mdi:image-outline";
  if (kind === "video") return "mdi:video-outline";
  if (kind === "audio") return "mdi:music-note-outline";
  if (kind === "document") return "mdi:file-document-outline";
  return "mdi:file-outline";
};

const formatBytes = (n) => {
  const size = Number(n) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const MediaLibraryLayer = () => {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("all");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const showAlert = (message, cls = "success") => {
    setAlert({ message, class: cls });
    setTimeout(() => setAlert(null), 2500);
  };

  const load = async (nextKind = kind) => {
    setLoading(true);
    try {
      const res = await apiCall({
        url: `features/media-library?kind=${encodeURIComponent(nextKind)}`,
        method: "GET",
      });
      if (res?.success) setRows(res.data || []);
    } catch {
      showAlert("Failed to load media library", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Media Library";
    load("all");
  }, []);

  const onKindChange = (id) => {
    setKind(id);
    load(id);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      showAlert("Choose a file first", "danger");
      return;
    }
    setSaving(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("title", title.trim() || file.name);
      const response = await apiCall({
        url: "features/media-library/upload",
        method: "POST",
        body,
        withoutStringify: true,
      });
      if (response?.success) {
        showAlert(response.msg || "Uploaded");
        setTitle("");
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
        load(kind);
      } else {
        showAlert(response?.msg || "Upload failed", "danger");
      }
    } catch (err) {
      showAlert(err.message || "Upload failed", "danger");
    } finally {
      setSaving(false);
    }
  };

  const saveTitle = async (row) => {
    if (!editTitle.trim()) {
      showAlert("Title required", "danger");
      return;
    }
    try {
      const response = await apiCall({
        url: "features/media-library/update",
        method: "POST",
        body: { id: row.id, title: editTitle.trim() },
      });
      if (response?.success) {
        showAlert(response.msg || "Updated");
        setEditingId(null);
        load(kind);
      } else showAlert(response?.msg || "Update failed", "danger");
    } catch (err) {
      showAlert(err.message, "danger");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.title}”?`)) return;
    try {
      const response = await apiCall({
        url: "features/media-library/del",
        method: "POST",
        body: { id: row.id },
      });
      if (response?.success) {
        showAlert(response.msg || "Deleted");
        load(kind);
      } else showAlert(response?.msg || "Delete failed", "danger");
    } catch (err) {
      showAlert(err.message, "danger");
    }
  };

  const copyUrl = async (row) => {
    const url = mediaUrl(row.url) || row.url;
    try {
      await navigator.clipboard.writeText(url);
      showAlert("URL copied");
    } catch {
      showAlert("Could not copy URL", "danger");
    }
  };

  return (
    <div className="cf-wrap">
      {alert && (
        <div className={`alert alert-${alert.class} cf-alert`}>{alert.message}</div>
      )}

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="cf-card">
            <div className="cf-card-head">
              <h5>Upload asset</h5>
              <p>Store logos, PDFs, images, and audio for reuse in messaging.</p>
            </div>
            <form onSubmit={handleUpload} className="cf-form">
              <label>
                Title
                <input
                  className="form-control"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brand logo"
                />
              </label>
              <label>
                File
                <input
                  ref={fileRef}
                  type="file"
                  className="form-control"
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  required
                />
              </label>
              {file ? (
                <div className="cf-meta-line">
                  <Icon icon="mdi:paperclip" width={16} />
                  {file.name} · {formatBytes(file.size)}
                </div>
              ) : null}
              <button type="submit" className="btn btn-success" disabled={saving}>
                {saving ? "Uploading…" : "Upload to library"}
              </button>
            </form>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="cf-card">
            <div className="cf-card-head d-flex flex-wrap align-items-start justify-content-between gap-2">
              <div>
                <h5>Media library</h5>
                <p>Your branded assets — copy URL to use in campaigns or replies.</p>
              </div>
              <div className="cf-kind-filters">
                {KINDS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`cf-kind-btn${kind === item.id ? " active" : ""}`}
                    onClick={() => onKindChange(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="cf-empty">Loading assets…</div>
            ) : rows.length === 0 ? (
              <div className="cf-empty">
                <Icon icon="mdi:folder-image-outline" width={28} />
                <p>No assets yet. Upload your first branded file.</p>
              </div>
            ) : (
              <div className="cf-media-grid">
                {rows.map((row) => {
                  const src = mediaUrl(row.url) || row.url;
                  const isImage = row.media_kind === "image";
                  return (
                    <div key={row.id} className="cf-media-card">
                      <div className="cf-media-preview">
                        {isImage ? (
                          <img src={src} alt={row.title} />
                        ) : (
                          <Icon icon={kindIcon(row.media_kind)} width={32} />
                        )}
                      </div>
                      <div className="cf-media-meta">
                        {editingId === row.id ? (
                          <div className="cf-media-edit">
                            <input
                              className="form-control form-control-sm"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={() => saveTitle(row)}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-light"
                              onClick={() => setEditingId(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <strong title={row.title}>{row.title}</strong>
                            <span>
                              {row.media_kind} · {formatBytes(row.file_size)}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="cf-media-actions">
                        <button type="button" title="Copy URL" onClick={() => copyUrl(row)}>
                          <Icon icon="mdi:content-copy" width={16} />
                        </button>
                        <a href={src} target="_blank" rel="noreferrer" title="Open">
                          <Icon icon="mdi:open-in-new" width={16} />
                        </a>
                        <button
                          type="button"
                          title="Rename"
                          onClick={() => {
                            setEditingId(row.id);
                            setEditTitle(row.title || "");
                          }}
                        >
                          <Icon icon="mdi:pencil-outline" width={16} />
                        </button>
                        <button type="button" title="Delete" className="danger" onClick={() => remove(row)}>
                          <Icon icon="mdi:trash-can-outline" width={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaLibraryLayer;
